import {
  Badge,
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiAlertTriangle, FiAlertCircle, FiInfo } from 'react-icons/fi';
import type { ProfileAlert } from '@/lib/sdk/profiling';

interface AlertsBadgeProps {
  alerts: ProfileAlert[];
}

interface AlertItemProps {
  alert: ProfileAlert;
}

const severityConfig = {
  info: {
    icon: FiInfo,
    colorScheme: 'blue',
    label: 'Info',
  },
  warning: {
    icon: FiAlertTriangle,
    colorScheme: 'orange',
    label: 'Warning',
  },
  critical: {
    icon: FiAlertCircle,
    colorScheme: 'red',
    label: 'Critical',
  },
};

function AlertItem({ alert }: AlertItemProps) {
  const config = severityConfig[alert.severity];
  const bgColor = useColorModeValue(
    `${config.colorScheme}.50`,
    `${config.colorScheme}.900`
  );
  const borderColor = useColorModeValue(
    `${config.colorScheme}.200`,
    `${config.colorScheme}.700`
  );

  return (
    <Box
      p={3}
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      w="full"
    >
      <HStack spacing={3} align="flex-start">
        <Icon
          as={config.icon}
          boxSize={5}
          color={`${config.colorScheme}.500`}
          mt={0.5}
        />
        <VStack align="start" spacing={1} flex={1}>
          <HStack>
            <Badge colorScheme={config.colorScheme} size="sm">
              {config.label}
            </Badge>
            <Text color="text-secondary" fontSize="xs">
              {alert.metric}
            </Text>
          </HStack>
          <Text color="text-primary" fontSize="sm">
            {alert.message}
          </Text>
          <Text color="text-muted" fontSize="xs">
            Current: {alert.current_value.toLocaleString()} | Threshold: {alert.threshold.toLocaleString()}
          </Text>
        </VStack>
      </HStack>
    </Box>
  );
}

export function AlertsBadge({ alerts }: AlertsBadgeProps) {
  if (!alerts || alerts.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Icon as={FiInfo} boxSize={8} color="green.500" mb={2} />
        <Text color="text-secondary" fontSize="sm">
          No alerts detected
        </Text>
      </Box>
    );
  }

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  return (
    <VStack spacing={4} align="stretch">
      <HStack spacing={2}>
        {criticalCount > 0 && (
          <Tooltip label={`${criticalCount} critical alert(s)`}>
            <Badge colorScheme="red" variant="solid" px={2} py={1}>
              {criticalCount} Critical
            </Badge>
          </Tooltip>
        )}
        {warningCount > 0 && (
          <Tooltip label={`${warningCount} warning(s)`}>
            <Badge colorScheme="orange" variant="solid" px={2} py={1}>
              {warningCount} Warning
            </Badge>
          </Tooltip>
        )}
        {alerts.length - criticalCount - warningCount > 0 && (
          <Badge colorScheme="blue" variant="solid" px={2} py={1}>
            {alerts.length - criticalCount - warningCount} Info
          </Badge>
        )}
      </HStack>
      <VStack spacing={2} align="stretch">
        {alerts.map((alert, index) => (
          <AlertItem key={index} alert={alert} />
        ))}
      </VStack>
    </VStack>
  );
}
